import { useEffect, useState } from 'react';

import { Link, useLocation } from 'react-router';
import { X } from 'lucide-react';

import { useCurrentUser, useUserDocument } from '@chartai/api-client';

interface TrialBannerState {
  visible: boolean;
  message: string;
  variant: 'info' | 'warning' | 'error';
  persistDismiss: boolean;
  dismissKey: string;
}

function getDaysLeft(expiryDate: Date): number {
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function getBannerState(
  uid: string,
  trialExpiryDate: Date | undefined,
  subscription: { status?: string } | undefined,
): TrialBannerState | null {
  if (subscription?.status === 'active') {
    return null;
  }

  if (!trialExpiryDate) {
    return null;
  }

  const daysLeft = getDaysLeft(trialExpiryDate);
  const expiryTime = trialExpiryDate.getTime();

  if (daysLeft >= 14) {
    return null;
  }

  if (daysLeft >= 8) {
    return {
      visible: true,
      message: `Your free trial ends in ${daysLeft} days. Subscribe now to keep your data.`,
      variant: 'info',
      persistDismiss: true,
      dismissKey: `trialBanner:${uid}:early:${expiryTime}`,
    };
  }

  if (daysLeft >= 4) {
    return {
      visible: true,
      message: `Your free trial ends in ${daysLeft} days. Don't lose access to your charts.`,
      variant: 'warning',
      persistDismiss: true,
      dismissKey: `trialBanner:${uid}:mid:${expiryTime}`,
    };
  }

  if (daysLeft >= 1) {
    return {
      visible: true,
      message: `Your free trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Subscribe to continue.`,
      variant: 'error',
      persistDismiss: false,
      dismissKey: '',
    };
  }

  if (subscription?.status === 'canceled') {
    return {
      visible: true,
      message: 'Your subscription is inactive. Resubscribe to regain access.',
      variant: 'error',
      persistDismiss: false,
      dismissKey: '',
    };
  }

  return {
    visible: true,
    message: 'Your free trial has expired. Subscribe to continue using ChartAI.',
    variant: 'error',
    persistDismiss: false,
    dismissKey: '',
  };
}

function getSubscriptionStatus(
  doc: Record<string, unknown>,
): { status?: string } | undefined {
  const sub = doc['subscription'];
  if (typeof sub !== 'object' || sub === null) {
    return undefined;
  }
  const status = 'status' in sub ? sub.status : undefined;
  return { status: typeof status === 'string' ? status : undefined };
}

const VARIANT_CLASSES: Record<string, string> = {
  info: 'bg-primary/10 text-primary border-primary/20',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  error: 'bg-destructive/10 text-destructive border-destructive/20',
};

export function TrialBanner() {
  const { user } = useCurrentUser();
  const { data: userDoc } = useUserDocument(user?.uid ?? null);
  const location = useLocation();
  const [sessionDismissed, setSessionDismissed] = useState(false);

  const bannerState = user && userDoc
    ? getBannerState(
        user.uid,
        userDoc['trialExpiryDate']
          ? new Date(String(userDoc['trialExpiryDate']))
          : undefined,
        getSubscriptionStatus(userDoc),
      )
    : null;

  const [storageDismissed, setStorageDismissed] = useState(false);

  useEffect(() => {
    if (bannerState?.persistDismiss && bannerState.dismissKey) {
      const dismissed = localStorage.getItem(bannerState.dismissKey);
      setStorageDismissed(dismissed === 'true');
    } else {
      setStorageDismissed(false);
    }
  }, [bannerState?.persistDismiss, bannerState?.dismissKey]);

  if (location.pathname.startsWith('/subscriptions') || location.pathname.startsWith('/preview')) {
    return null;
  }

  if (!bannerState || !bannerState.visible) {
    return null;
  }

  if (sessionDismissed) {
    return null;
  }

  if (bannerState.persistDismiss && storageDismissed) {
    return null;
  }

  const handleDismiss = () => {
    if (bannerState.persistDismiss && bannerState.dismissKey) {
      localStorage.setItem(bannerState.dismissKey, 'true');
      setStorageDismissed(true);
    } else {
      setSessionDismissed(true);
    }
  };

  return (
    <div
      className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm ${VARIANT_CLASSES[bannerState.variant] ?? ''}`}
      role="alert"
    >
      <p className="flex-1 font-medium">{bannerState.message}</p>
      <div className="flex items-center gap-3">
        <Link
          to="/subscriptions"
          className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-white hover:bg-primary/90"
        >
          Subscribe
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-md p-1 text-current opacity-60 transition-opacity hover:opacity-100"
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
